import { c as _c } from "react/compiler-runtime";
// `<OutcomeRow>` — stacked filled bar with outcome name + share %.
//
// Mirrors the in-app market card's row layout: a horizontal "track"
// with a coloured bar filled to `share` width, the outcome name on the
// left, and the share % on the right.
//
// Use stacked vertically (top-down) with a small gap (~12px) — the
// stacking is the consensus visualisation. For 2-outcome markets, top-2;
// for N-outcome markets, callers pick top-K and render whatever they
// want for the rest.
//
// Satori-friendly: single root, explicit `display: 'flex'`, inline-style
// only. The fill bar is an absolutely-positioned div inside the track —
// Satori supports this when both axes are explicit.

import type { CSSProperties, JSX } from 'react';
import { formatOgPercent } from '@/lib/currency/server';
import { OG_CONTENT_WIDTH } from '../tokens';
export interface OutcomeRowProps {
  /** Outcome label / name (e.g. "Yes", "Bullish", "Team A"). */
  label: string;
  /** Consensus share, normalised 0..1. The bar fills to this fraction. */
  share: number;
  /** Bar fill colour (typically from `getOutcomeColorOg(label)`). */
  color: string;
  /** Total row width in pixels. Default `OG_CONTENT_WIDTH`. */
  width?: number;
  /** Total row height in pixels. Default 64. */
  height?: number;
  /** Winning outcome of a resolved/graduated market. Renders the fill at
   *  full opacity and overlays an inset highlight ring so the winner reads
   *  unambiguously even at Twitter thumbnail size. */
  isWinner?: boolean;
}
const TRACK_BG = 'rgba(255, 255, 255, 0.08)';
const FILL_OPACITY = 0.85;
const WINNER_FILL_OPACITY = 1;
const WINNER_RING = 'rgba(255, 255, 255, 0.35)';
const TEXT_COLOR = '#fafafa';
/** Reserved width on the right for the share % column — share text
 *  ("100%") + label-padding + share-padding. Label clips with ellipsis
 *  before encroaching this zone. */
const SHARE_RESERVED_PX = 130;
export function OutcomeRow(props) {
  const $ = _c(35);
  const width = props.width ?? OG_CONTENT_WIDTH;
  const height = props.height ?? 64;
  const isWinner = props.isWinner === true;
  const fillWidth = Math.max(0, Math.min(1, props.share)) * width;
  const labelMaxWidth = Math.max(0, width - 24 - SHARE_RESERVED_PX);
  let t0;
  if ($[0] !== height || $[1] !== width) {
    t0 = {
      display: "flex",
      position: "relative",
      width,
      height,
      background: TRACK_BG,
      borderRadius: 12,
      overflow: "hidden"
    };
    $[0] = height;
    $[1] = width;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const trackStyle = t0;
  const t1 = isWinner ? WINNER_FILL_OPACITY : FILL_OPACITY;
  let t2;
  if ($[3] !== fillWidth || $[4] !== height || $[5] !== props.color || $[6] !== t1) {
    t2 = {
      display: "flex",
      position: "absolute",
      left: 0,
      top: 0,
      width: fillWidth,
      height,
      background: props.color,
      opacity: t1,
      borderRadius: 12
    };
    $[3] = fillWidth;
    $[4] = height;
    $[5] = props.color;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const fillStyle = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      position: "absolute",
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      borderRadius: 12,
      border: `2px solid ${WINNER_RING}`
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const winnerRingStyle = t3;
  let t4;
  if ($[9] !== height) {
    t4 = height.toString();
    $[9] = height;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `${t4}px`;
  let t6;
  if ($[11] !== height || $[12] !== labelMaxWidth || $[13] !== t5) {
    t6 = {
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      position: "absolute",
      left: 24,
      top: 0,
      height,
      lineHeight: t5,
      maxWidth: labelMaxWidth,
      fontSize: 28,
      fontWeight: 600,
      color: TEXT_COLOR
    };
    $[11] = height;
    $[12] = labelMaxWidth;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const labelStyle = t6;
  let t7;
  if ($[15] !== height) {
    t7 = {
      display: "flex",
      position: "absolute",
      right: 24,
      top: 0,
      height,
      alignItems: "center",
      fontSize: 28,
      fontWeight: 600,
      color: TEXT_COLOR
    };
    $[15] = height;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const shareStyle = t7;
  let t8;
  if ($[17] !== fillStyle) {
    t8 = <div style={fillStyle} />;
    $[17] = fillStyle;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isWinner) {
    t9 = isWinner && <div style={winnerRingStyle} />;
    $[19] = isWinner;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== labelStyle || $[22] !== props.label) {
    t10 = <div style={labelStyle}>{props.label}</div>;
    $[21] = labelStyle;
    $[22] = props.label;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== props.share) {
    t11 = formatOgPercent(props.share);
    $[24] = props.share;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== shareStyle || $[27] !== t11) {
    t12 = <span style={shareStyle}>{t11}</span>;
    $[26] = shareStyle;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t8 || $[32] !== t9 || $[33] !== trackStyle) {
    t13 = <div style={trackStyle}>{t8}{t9}{t10}{t12}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t8;
    $[32] = t9;
    $[33] = trackStyle;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}